import React, { useState, useEffect, useCallback } from 'react';
import { Users, Search, Plus, Edit, Trash2, CheckCircle, XCircle, AlertCircle, Shield } from 'lucide-react';
import { Link } from 'react-router-dom';
import { adminApi, authApi } from '../services/api';
import { User } from '../types';
import { showSuccess, showError, confirmAsync } from '../utils/toast';
import { extractApiError } from '../utils/errorUtils';

export default function AdminUserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentUser, setCurrentUser] = useState<User | null>(null);

  // Pagination & filters
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [emailVerifiedFilter, setEmailVerifiedFilter] = useState<string>('');

  // Modals
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);

  // Form state
  const [formEmail, setFormEmail] = useState('');
  const [formPassword, setFormPassword] = useState('');
  const [formLoading, setFormLoading] = useState(false);

  const pageSize = 20;

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [emailVerifiedFilter]);

  // Get current user
  useEffect(() => {
    const loadCurrentUser = async () => {
      try {
        const user = await authApi.getCurrentUser();
        setCurrentUser(user);
      } catch (err) {
        console.error('Failed to load current user:', err);
      }
    };
    loadCurrentUser();
  }, []);

  // Load users
  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const params: {
        page: number;
        page_size: number;
        search?: string;
        email_verified?: boolean;
      } = {
        page,
        page_size: pageSize,
      };

      if (debouncedSearch) params.search = debouncedSearch;
      if (emailVerifiedFilter === 'verified') params.email_verified = true;
      if (emailVerifiedFilter === 'unverified') params.email_verified = false;

      const response = await adminApi.getAllUsers(params);
      // Response is PaginatedResponse when called with params
      const paginatedResponse = response as { items: User[]; total: number; total_pages: number };
      setUsers(paginatedResponse.items);
      setTotal(paginatedResponse.total);
      setTotalPages(paginatedResponse.total_pages);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      setError(apiError.message);
      console.error('Error loading users:', apiError);
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, emailVerifiedFilter]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Create user
  const handleCreateUser = async () => {
    if (!formEmail || !formPassword) {
      showError('Email and password are required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formEmail)) {
      showError('Please enter a valid email address');
      return;
    }

    // Password strength validation
    if (formPassword.length < 8) {
      showError('Password must be at least 8 characters long');
      return;
    }

    try {
      setFormLoading(true);
      await adminApi.createUser({
        email: formEmail,
        password: formPassword,
      });
      showSuccess('User created successfully');
      setCreateModalOpen(false);
      setFormEmail('');
      setFormPassword('');
      loadUsers();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      showError('Failed to create user', {
        description: apiError.message,
      });
      console.error('Error creating user:', apiError);
    } finally {
      setFormLoading(false);
    }
  };

  // Edit user
  const handleEditUser = async () => {
    if (!selectedUser) return;

    // Validate email if changed
    if (formEmail !== selectedUser.email) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formEmail)) {
        showError('Please enter a valid email address');
        return;
      }
    }

    // Validate password if provided
    if (formPassword && formPassword.length < 8) {
      showError('Password must be at least 8 characters long');
      return;
    }

    try {
      setFormLoading(true);
      const updates: { email?: string; password?: string } = {};
      if (formEmail !== selectedUser.email) updates.email = formEmail;
      if (formPassword) updates.password = formPassword;

      await adminApi.updateUser(selectedUser.id, updates);
      showSuccess('User updated successfully');
      setEditModalOpen(false);
      setSelectedUser(null);
      setFormEmail('');
      setFormPassword('');
      loadUsers();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      showError('Failed to update user', {
        description: apiError.message,
      });
      console.error('Error updating user:', apiError);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete user
  const handleDeleteUser = async (user: User) => {
    // Prevent self-deletion
    if (currentUser && user.id === currentUser.id) {
      showError('Cannot delete your own account', {
        description: 'Please have another admin delete this account if needed.',
      });
      return;
    }

    const confirmed = await confirmAsync(
      `Delete user "${user.email}"?`,
      {
        description: 'This action cannot be undone. The user may have associated data (reservations, vendor profiles, etc.).',
        confirmLabel: 'Delete User',
        cancelLabel: 'Cancel',
      }
    );

    if (!confirmed) return;

    try {
      await adminApi.deleteUser(user.id);
      showSuccess('User deleted successfully');
      loadUsers();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      showError('Failed to delete user', {
        description: apiError.message,
      });
      console.error('Error deleting user:', apiError);
    }
  };

  // Verify email
  const [verifyingUserId, setVerifyingUserId] = useState<number | null>(null);
  const handleVerifyEmail = async (user: User) => {
    try {
      setVerifyingUserId(user.id);
      await adminApi.verifyUserEmail(user.id);
      showSuccess('Email verified successfully');
      loadUsers();
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      showError('Failed to verify email', {
        description: apiError.message,
      });
      console.error('Error verifying email:', apiError);
    } finally {
      setVerifyingUserId(null);
    }
  };

  // Open edit modal
  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setFormEmail(user.email);
    setFormPassword('');
    setEditModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4" data-testid="admin-user-management">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2" data-testid="users-title">
            User Management
          </h1>
          <p className="text-gray-600">
            Manage users, verify emails, and assign roles
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-800 rounded-md p-4 flex items-center" data-testid="users-error">
            <AlertCircle className="h-5 w-5 mr-2 flex-shrink-0" />
            {error}
          </div>
        )}

        {/* Filters & Actions */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row gap-4 flex-1">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                data-testid="users-search-input"
                placeholder="Search by email or name..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
              />
            </div>

            {/* Email Verified Filter */}
            <div>
              <label htmlFor="verified-filter" className="sr-only">
                Filter by verification status
              </label>
              <select
                id="verified-filter"
                data-testid="users-verified-filter"
                value={emailVerifiedFilter}
                onChange={(e) => setEmailVerifiedFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                aria-label="Filter users by email verification status"
              >
                <option value="">All Users</option>
                <option value="verified">Verified</option>
                <option value="unverified">Unverified</option>
              </select>
            </div>
          </div>

          {/* Create Button */}
          <button
            type="button"
            data-testid="users-create-button"
            onClick={() => {
              setFormEmail('');
              setFormPassword('');
              setCreateModalOpen(true);
            }}
            className="flex items-center justify-center space-x-2 bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark"
          >
            <Plus className="h-5 w-5" />
            <span>Create User</span>
          </button>
        </div>

        {/* Total Count */}
        <div className="mb-4 text-sm text-gray-600" data-testid="users-total">
          Total: {total} user{total !== 1 ? 's' : ''}
        </div>

        {/* Loading */}
        {loading && (
          <div className="text-center py-12" data-testid="users-loading">
            <div className="text-gray-600">Loading users...</div>
          </div>
        )}

        {/* Empty State */}
        {!loading && users.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow" data-testid="users-empty">
            <Users className="h-16 w-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Found</h3>
            <p className="text-gray-600">
              {search || emailVerifiedFilter
                ? 'Try adjusting your filters.'
                : 'Create your first user to get started.'}
            </p>
          </div>
        )}

        {/* Users Table */}
        {!loading && users.length > 0 && (
          <div className="bg-white shadow-md rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200" data-testid="users-table">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Email
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Roles
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((user) => (
                    <tr key={user.id} data-testid={`user-row-${user.id}`}>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{user.email}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {user.email_verified ? (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Verified
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                            <XCircle className="h-3 w-3 mr-1" />
                            Unverified
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex flex-wrap gap-1">
                          {user.roles && user.roles.length > 0 ? (
                            user.roles.map((role, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-moonbone text-ink"
                              >
                                {role}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-gray-500">No roles</span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {user.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex items-center justify-end space-x-2">
                          {/* Verify Email */}
                          {!user.email_verified && (
                            <button
                              type="button"
                              onClick={() => handleVerifyEmail(user)}
                              className="text-green-600 hover:text-green-900 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Verify Email"
                              aria-label={`Verify email for ${user.email}`}
                              data-testid={`user-verify-${user.id}`}
                              disabled={verifyingUserId === user.id}
                            >
                              <CheckCircle className="h-5 w-5" />
                            </button>
                          )}

                          {/* Link to Role Management */}
                          <Link
                            to="/admin/roles"
                            state={{ userId: user.id }}
                            className="text-secondary hover:text-ink"
                            title="Manage Roles"
                            aria-label={`Manage roles for ${user.email}`}
                            data-testid={`user-roles-${user.id}`}
                          >
                            <Shield className="h-5 w-5" />
                          </Link>

                          {/* Edit */}
                          <button
                            type="button"
                            onClick={() => openEditModal(user)}
                            className="text-secondary hover:text-teal-900"
                            title="Edit User"
                            aria-label={`Edit user ${user.email}`}
                            data-testid={`user-edit-${user.id}`}
                          >
                            <Edit className="h-5 w-5" />
                          </button>

                          {/* Delete */}
                          <button
                            type="button"
                            onClick={() => handleDeleteUser(user)}
                            className="text-red-600 hover:text-red-900 disabled:opacity-50 disabled:cursor-not-allowed"
                            title="Delete User"
                            aria-label={`Delete user ${user.email}`}
                            data-testid={`user-delete-${user.id}`}
                            disabled={currentUser?.id === user.id}
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    type="button"
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    aria-label={`Go to previous page (current page ${page} of ${totalPages})`}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page === totalPages}
                    aria-label={`Go to next page (current page ${page} of ${totalPages})`}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700" aria-live="polite">
                      Showing page <span className="font-medium">{page}</span> of{' '}
                      <span className="font-medium">{totalPages}</span>
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        type="button"
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        aria-label={`Go to previous page (current page ${page} of ${totalPages})`}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <button
                        type="button"
                        onClick={() => setPage(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        aria-label={`Go to next page (current page ${page} of ${totalPages})`}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Create User Modal */}
      {createModalOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => setCreateModalOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-labelledby="create-user-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="create-user-title" className="text-xl font-bold text-gray-900 mb-4">Create New User</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="create-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-email"
                  type="email"
                  data-testid="create-user-email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div>
                <label htmlFor="create-password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password <span className="text-red-500">*</span>
                </label>
                <input
                  id="create-password"
                  type="password"
                  data-testid="create-user-password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                  required
                />
              </div>
              <div className="bg-moonbone border border-teal-200 rounded-md p-3">
                <p className="text-sm text-ink">
                  <strong>Note:</strong> To assign roles, create the user first, then use the Role Management page. Password must be at least 8 characters.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => setCreateModalOpen(false)}
                disabled={formLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="create-user-submit"
                onClick={handleCreateUser}
                disabled={formLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {formLoading ? 'Creating...' : 'Create User'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editModalOpen && selectedUser && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50"
          onClick={() => {
            setEditModalOpen(false);
            setSelectedUser(null);
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="edit-user-title"
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-md w-full p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="edit-user-title" className="text-xl font-bold text-gray-900 mb-4">Edit User</h2>
            <div className="space-y-4">
              <div>
                <label htmlFor="edit-email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  id="edit-email"
                  type="email"
                  data-testid="edit-user-email"
                  value={formEmail}
                  onChange={(e) => setFormEmail(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
              <div>
                <label htmlFor="edit-password" className="block text-sm font-medium text-gray-700 mb-1">
                  New Password (leave blank to keep current)
                </label>
                <input
                  id="edit-password"
                  type="password"
                  data-testid="edit-user-password"
                  value={formPassword}
                  onChange={(e) => setFormPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary"
                />
              </div>
              <div className="bg-moonbone border border-teal-200 rounded-md p-3">
                <p className="text-sm text-ink">
                  <strong>Note:</strong> To manage roles, use the Shield icon or visit the Role Management page.
                </p>
              </div>
            </div>
            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={() => {
                  setEditModalOpen(false);
                  setSelectedUser(null);
                }}
                disabled={formLoading}
                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="edit-user-submit"
                onClick={handleEditUser}
                disabled={formLoading}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark disabled:opacity-50"
              >
                {formLoading ? 'Updating...' : 'Update User'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
